import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Copy, Loader2, MessageCircle } from 'lucide-react';
import { API } from '../../../lib/api';
import { waUrl } from '../../../lib/waShare';
import { FilePicker, uploadAll } from './SwitchFiles';
import { switchCopy } from './switch';

const fieldCls = 'w-full rounded-xl border border-[#cbd5e1] bg-[#ffffff] px-3.5 py-2.5 text-[15px] text-[#0f172a] outline-none transition-colors focus:border-[#0f766e]';
const EMPTY = { workshop_name: '', owner_name: '', phone: '', email: '', city: '', current_software: '', bays: '', customer_count: '', go_live_date: '' };
const FIELDS = [['workshop_name', 'text', true], ['owner_name', 'text', true], ['phone', 'tel', true], ['email', 'email', false],
  ['city', 'text', false], ['current_software', 'text', false], ['bays', 'number', false], ['customer_count', 'text', false], ['go_live_date', 'date', false]];

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg?.replace('Value error, ', '')).join(' ') : d || fallback;
};

const Done = ({ result, failed, t }) => {
  const [copied, setCopied] = useState(false);
  const url = result.tracker_url;
  const msg = `${t.wa_msg} ${url}`;
  return (
    <div className="rounded-3xl bg-[#ffffff] p-6 sm:p-8" data-testid="switch-done">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ccfbf1] text-[#0f766e]"><Check size={22} /></span>
      <h3 className="dl-display mt-4 text-2xl font-semibold text-[#0f172a]">{t.done_title}</h3>
      <p className="mt-2 text-[#475569]">{t.done_sub}</p>
      {failed > 0 && <p className="mt-2 text-sm text-[#b45309]" data-testid="switch-done-failed">{failed} {t.done_failed}</p>}
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#f1f5f9] px-3 py-2.5"><span className="min-w-0 flex-1 truncate font-mono text-xs text-[#334155]" data-testid="switch-tracker-url">{url}</span>
        <button type="button" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))} className="text-[#0f766e]" aria-label="Copy link" data-testid="switch-copy-link">{copied ? <Check size={16} /> : <Copy size={16} />}</button></div>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link to={`/driveline/switch/track/${result.code}`} className="inline-flex items-center gap-2 rounded-full bg-[#0f172a] px-5 py-3 text-sm font-semibold text-[#f8fafc]" data-testid="switch-open-tracker">{t.open_tracker} <ArrowRight size={15} /></Link>
        <a href={waUrl('', msg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#cbd5e1] px-5 py-3 text-sm font-semibold text-[#0f172a]" data-testid="switch-wa-save">
          <MessageCircle size={15} /> {t.wa_save}</a>
      </div>
    </div>
  );
};

export const SwitchForm = ({ lang = 'en' }) => {
  const t = switchCopy(lang).form;
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/public/driveline/switch`, { ...form, bays: form.bays === '' ? null : Number(form.bays), lang });
      setFailed(await uploadAll(files, `${API}/public/driveline/switch/${data.code}/files`, setProgress));
      setResult(data);
    } catch (ex) { setErr(errText(ex, t.error)); } finally { setBusy(false); }
  };

  if (result) return <Done result={result} failed={failed} t={t} />;
  return (
    <form onSubmit={submit} className="space-y-4 rounded-3xl bg-[#ffffff]/70 p-5 sm:p-8" data-testid="switch-form">
      <div className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map(([k, type, req]) => (
          <label key={k} className={k === 'current_software' ? 'sm:col-span-2' : ''}>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#64748b]">{t.fields[k]}{req && ' *'}</span>
            <input type={type} required={req} value={form[k]} min={type === 'number' ? 0 : undefined} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={fieldCls} data-testid={`switch-${k}`} />
          </label>
        ))}
      </div>
      <FilePicker files={files} setFiles={setFiles} busy={busy} progress={progress} t={t} />
      <p className="text-xs text-[#64748b]">{t.no_files}</p>
      {err && <p className="rounded-xl bg-[#fff1f2] px-3 py-2 text-sm text-[#be123c]" data-testid="switch-error">{err}</p>}
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#0f766e] px-6 py-3.5 text-[15px] font-semibold text-[#ffffff] transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="switch-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {busy ? t.sending : t.submit}
      </button>
    </form>
  );
};
